import gql from 'graphql-tag'
import { MutationOptions, QueryOptions, SubscriptionOptions } from 'apollo-client'
import { apolloProvider } from 'boot/apollo'
import { AssetClassEnum, GeneralStatusEnum, MessageTypeEnum } from 'able-lib'
import { ROLES } from 'src/lib/constants'

export function fetchContactList ({ userId, roleIds, groupType, offset, limit, search = '' }: {userId: number; roleIds: number[]; groupType: string[]; offset: number; limit: number; search?: string}) {
  const options: QueryOptions = {
    query: gql`
      query fetchContactList($userId: Int!, $roleIds: [Int!]!, $groupType: [enum_group_type_enum!], $offset: Int, $limit: Int, $search: String!) {
        users: search_user(args: {search: $search}, where: {id: {_neq: $userId}, _or: [{user_roles: {role_id: {_in: $roleIds}}, user_groups: {group: {type: {_in: $groupType}, user_groups: {user_id: {_eq: $userId}}}}}, {user_roles: {role_id: {_in: $roleIds}}}]}, offset: $offset, limit: $limit) {
          id
          firstName: firstname
          lastName: lastname
          roles: user_roles(where: {role_id: {_in: $roleIds}}) {
            role {
              id
              name
            }
          }
        }
      }
    `,
    variables: {
      userId,
      roleIds,
      groupType,
      offset,
      limit,
      search
    }
  }

  return apolloProvider.defaultClient.query(options)
}

export function getConversations (userIds: Array<number>) {
  const options: QueryOptions = {
    query: gql`
      query getConversations($userIds: [Int!]!) {
        conversations: group(where: {user_groups: { user_id: { _in: $userIds }}}) {
          id
          name
        }
      }
    `,
    variables: {
      userIds
    }
  }

  return apolloProvider.defaultClient.query(options)
}

export function fetchConversations (userId: number) {
  const options: SubscriptionOptions = {
    query: gql`
      subscription fetchConversations($userId: Int!) {
        conversations: group(where: {type: {_in: [DIRECT_MESSAGE, GROUP_MESSAGE]}, user_groups: {user_id: {_eq: $userId}}}, order_by: {updated_at: desc}) {
          id
          name
          type
          messages: message_groups(where: {message: {type: {_nin: [NOTIFICATION]}}}, order_by: {message: {created_at: desc}}, limit: 1) {
            message {
              id
              type
              createdAt: created_at
              sender: user {
                id
                firstName: firstname
                lastName: lastname
              }
              replyMessage: message {
                id
                type
                createdAt: created_at
                sender: user {
                  id
                  firstName: firstname
                  lastName: lastname
                }
                content {
                  body
                  files: content_assets {
                    asset {
                      id
                      url
                      type
                    }
                  }
                }
              }
              content {
                body
                files: content_assets {
                  asset {
                    id
                    url
                    description
                    type
                  }
                }
              }
            }
          }
          messages_aggregate: message_groups_aggregate(where: {message: {type: {_nin: [NOTIFICATION]}}}) {
            aggregate {
              count
            }
          }
          users: user_groups {
            user {
              id
              firstName: firstname
              lastName: lastname
              roles: user_roles {
                role {
                  name
                }
              }
            }
          }
          user_config: user_groups(where: {user_id: {_eq: $userId}}) {
            lastSeen: updated_at
            user {
              unread_messages: user_unread_messages {
                count: unread_message_count
              }
              unread_messages_aggregate: user_unread_messages_aggregate {
                aggregate {
                  sum {
                    unread_message_count
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      userId
    }
  }

  return apolloProvider.defaultClient.subscribe(options)
}

export function fetchMessagesOnConversation (conversationId: number, lastMessageId: number, limit: number) {
  const options: QueryOptions = {
    query: gql`
      query getMessages($conversationId: Int!, $lastMessageId: Int, $limit: Int) {
        messages: message(where: {id: {_lt: $lastMessageId}, message_groups: {group_id: {_eq: $conversationId}}}, limit: $limit, order_by: {created_at: desc}) {
          id
          type
          createdAt: created_at
          sender: user {
            id
            firstName: firstname
            lastName: lastname
          }
          replyMessage: message {
            id
            type
            createdAt: created_at
            sender: user {
              id
              firstName: firstname
              lastName: lastname
            }
            content {
              body
              files: content_assets {
                asset {
                  id
                  url
                  type
                }
              }
            }
          }
          content {
            body
            files: content_assets {
              asset {
                id
                url
                description
                type
              }
            }
          }
        }
      }
    `,
    variables: {
      conversationId,
      lastMessageId,
      limit
    },
    fetchPolicy: 'no-cache'
  }

  return apolloProvider.defaultClient.query(options)
}

export function updateConversationLastSeen (userId: number, groupId: number) {
  const options: MutationOptions = {
    mutation: gql`
      mutation updateConversationLastSeen ($userId: Int!, $groupId: Int!, $timestamptz: timestamptz) {
        update_user_group(where: {user_id: {_eq: $userId}, group_id: {_eq: $groupId}}, _set: {updated_at: $timestamptz}) {
          affected_rows
        }
      }
    `,
    variables: {
      userId,
      groupId,
      timestamptz: new Date().toDateString()
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}

export function insertMessageOnConversation (senderId, message: { roomId: number; content: string; files: Array<any>; replyMessage: any; users: Array<any> }) {
  const contentAssetData = message.files.map(file => {
    return {
      asset: {
        classification: AssetClassEnum.ATTACHMENT,
        description: file.description,
        type: file.type,
        url: file.url,
        is_shared: false
      }
    }
  })

  const data = {
    type: MessageTypeEnum.MESSAGE,
    status: GeneralStatusEnum.PUBLISHED,
    sender_user_id: senderId,
    parent_message_id: message.replyMessage ? message.replyMessage.id : null,
    content: {
      data: {
        body: message.content,
        content_assets: {
          data: contentAssetData
        }
      }
    },
    message_groups: {
      data: {
        group_id: message.roomId
      }
    }
  }

  const options: MutationOptions = {
    mutation: gql`
      mutation insertMessageOnConversation($userId: Int!, $groupId: Int!, $dataObject: message_insert_input!, $timestamptz: timestamptz) {
        newMessage: insert_message_one(object: $dataObject) {
          id
          messages: message_groups {
            conversationId: group_id
          }
        }
        update_group_by_pk(pk_columns: {id: $groupId}, _set: {updated_at: $timestamptz}) {
          id
        }
        update_user_group(where: {user_id: {_eq: $userId}, group_id: {_eq: $groupId}}, _set: {updated_at: $timestamptz}) {
          affected_rows
        }
      }
    `,
    variables: {
      userId: senderId,
      groupId: message.roomId,
      dataObject: data,
      timestamptz: new Date().toDateString()
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}

export function insertNewConversation (senderId: number, message: { roomId: number; content: string; files: Array<any>; replyMessage: any; users: Array<any> }) {
  const contentAssetData = message.files.map(file => {
    return {
      asset: {
        classification: AssetClassEnum.ATTACHMENT,
        description: file.description,
        type: file.type,
        url: file.url,
        is_shared: false
      }
    }
  })

  const dataObject = {
    name: '',
    type: 'ADHOC',
    user_groups: {
      data: []
    },
    message_groups: {
      data: {
        message: {
          data: {
            type: MessageTypeEnum.MESSAGE,
            status: GeneralStatusEnum.PUBLISHED,
            sender_user_id: senderId,
            parent_message_id: message.replyMessage ? message.replyMessage : null,
            content: {
              data: {
                body: message.content,
                content_assets: {
                  data: contentAssetData
                }
              }
            }
          }
        }
      }
    }
  }
  if (message.users.length === 1) {
    dataObject.name = 'MESSAGE_' + message.users[0].id + '_' + senderId
    dataObject.type = 'DIRECT_MESSAGE'
  } else if (message.users.length > 1) {
    dataObject.type = 'GROUP_MESSAGE'
  }

  dataObject.name = 'MESSAGE_' + senderId

  for (const user of message.users) {
    dataObject.name += '_' + user.id
    dataObject.user_groups.data.push({
      user_id: user.id,
      role_id: ROLES.PARTICIPANT.id
    })
  }

  dataObject.user_groups.data.push({
    user_id: senderId,
    role_id: ROLES.PARTICIPANT.id
  })

  const options: MutationOptions = {
    mutation: gql`
      mutation newConversation($dataObject: group_insert_input!) {
        newConversation: insert_group_one(object: $dataObject) {
          id
        }
      }
    `,
    variables: {
      dataObject
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}

export function subscribeToAnnouncement (userId: number) {
  const options: SubscriptionOptions = {
    query: gql`
      subscription subscribeToAnnouncement($userId: Int!) {
        announcements: message(
          where: {
            type: {_eq: NOTIFICATION},
            _or: [
              {
                sender_user_id: {_eq: $userId}
              },
              {
                _not: {message_groups: {}}
              },
              {
                message_groups: {
                  group: {
                    user_groups: {
                      user_id: {_eq: $userId}}
                  }
                }
              }
            ]
          },
          order_by: {created_at: desc}
        ) {
          id
          type
          data
          metadata: user_message_notification_metadata(where: {user_id: {_eq: $userId}}) {
            userId: user_id
            isRead: is_read
            isAcknowledged: is_acknowledged
          }
          createdAt: created_at
          senderUser: user {
            id
            email
          }
          content {
            id
            title
            body
            contentAssets: content_assets {
              asset {
                id
                description
                class
                url
                type
              }
            }
          }
        }
      }
    `,
    variables: {
      userId
    },
    fetchPolicy: 'network-only'
  }

  return apolloProvider.defaultClient.subscribe(options)
}

export function subscribeUnreadAnnouncement (userId: number) {
  const options: SubscriptionOptions = {
    query: gql`
      subscription subscribeUnreadAnnouncement($userId: Int!) {
        announcements: message_aggregate(
          where: {
            type: {_eq: NOTIFICATION},
            _not: {
              user_message_notification_metadata: {
                user_id: {_eq: $userId}
              }
            },
            _or: [
              {
                _not: {message_groups: {}}
              },
              {
                message_groups: {
                  group: {
                    user_groups: {
                      user_id: {_eq: $userId}}
                  }
                }
              }
            ]
          }
        ) {
          aggregate {
            count
          }
        }
      }
    `,
    variables: {
      userId
    }
  }

  return apolloProvider.defaultClient.subscribe(options)
}

export function readMessageNotification (notificationId: number, userId: number) {
  const options: MutationOptions = {
    mutation: gql`
      mutation readMessageNotification($object: user_message_notification_metadata_insert_input!) {
        insert_user_message_notification_metadata_one(object: $object, on_conflict: {constraint: user_message_notification_metadata_pkey, update_columns: [is_read]}) {
          isRead: is_read
          isAcknowledged: is_acknowledged
        }
      }
    `,
    variables: {
      object: {
        is_read: true,
        is_acknowledged: false,
        user_id: userId,
        message_id: notificationId
      }
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}

export function getAnnouncement (announcementId: number, userId: number) {
  const options: QueryOptions = {
    query: gql`
      query getAnnouncement($messageId: Int!, $userId: Int!) {
        announcement: message_by_pk(id: $messageId) {
          id
          type
          status
          data
          metadata: user_message_notification_metadata(where: {user_id: {_eq: $userId}}) {
            userId: user_id
            isRead: is_read
            isAcknowledged: is_acknowledged
          }
          createdAt: created_at
          senderUser: user {
            id
            email
          }
          messageGroups: message_groups(where: {group: {type: {_eq: SECTION}}}) {
            group {
              id
              sections {
                sectionId: group_id
                levelId: level_id
              }
            }
          }
          content {
            id
            title
            body
            contentAssets: content_assets {
              asset {
                id
                url
                description
                type
                class
              }
            }
          }
        }
      }
    `,
    variables: {
      messageId: announcementId,
      userId
    }
  }

  return apolloProvider.defaultClient.query(options)
}

export function insertMessageAsNotification (notification: any, groups: any[]) {
  const contentAssetData = notification.files.map(file => {
    if (file.id) {
      return {
        asset_id: file.id
      }
    }
    return {
      asset: {
        data: {
          class: AssetClassEnum.ATTACHMENT,
          description: file.description,
          type: file.type,
          url: file.url,
          is_shared: false
        }
      }
    }
  })

  const data = {
    type: MessageTypeEnum.NOTIFICATION,
    status: GeneralStatusEnum.PUBLISHED,
    sender_user_id: notification.senderId,
    parent_message_id: null,
    data: notification.data,
    content: {
      data: {
        title: notification.title,
        body: notification.content,
        content_assets: {
          data: contentAssetData
        }
      }
    },
    user_message_notification_metadata: {
      data: {
        is_read: true,
        is_acknowledged: notification.data.requireAcknowledge,
        user_id: notification.senderId
      }
    },
    message_groups: undefined
  }

  if (groups.length > 0) {
    data.message_groups = {
      data: groups.map(group => {
        return {
          group_id: group.sectionId
        }
      })
    }
  }

  const options: MutationOptions = {
    mutation: gql`
      mutation insertMessageAsNotification($dataObject: message_insert_input!) {
        newNotification: insert_message_one(object: $dataObject) {
          id
        }
      }
    `,
    variables: {
      dataObject: data
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}

export function updateMessageNotification (notification: any) {
  const remainingAssets = []
  const insertAssets = []
  const insertContentAssets = []
  for (const file of notification.files) {
    if (file.id) {
      if (!file.isShared) remainingAssets.push(file.id)
      insertContentAssets.push({
        content_id: notification.contentId,
        asset_id: file.id
      })
    } else {
      insertAssets.push({
        class: AssetClassEnum.ATTACHMENT,
        description: file.description,
        type: file.type,
        url: file.url,
        is_shared: false,
        content_assets: {
          data: {
            content_id: notification.contentId
          }
        }
      })
    }
  }

  const options: MutationOptions = {
    mutation: gql`
      mutation updateMessage($messageId: Int!, $remainingAssets: [Int!]!, $messageData: message_set_input, $contentData: content_set_input, $insertContentAssets: [content_asset_insert_input!]!, $insertAssets: [asset_insert_input!]!) {
        update_message_by_pk(pk_columns: {id: $messageId}, _set: $messageData) {
          id
        }
        update_content(where: {messages: {id: {_eq: $messageId}}}, _set: $contentData) {
          affected_rows
        }
        delete_content_asset(where: {asset_id: {_nin: $remainingAssets}, content: {messages: {id: {_eq: $messageId}}}}) {
          affected_rows
        }
        insert_content_asset(objects: $insertContentAssets, on_conflict: {constraint: content_asset_pkey, update_columns: asset_id}) {
          affected_rows
        }
        insert_asset(objects: $insertAssets) {
          affected_rows
        }
      }
    `,
    variables: {
      messageId: notification.id,
      remainingAssets,
      messageData: {
        data: notification.data
      },
      contentData: {
        title: notification.title,
        body: notification.content
      },
      insertContentAssets,
      insertAssets
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}

export function getUnAcknowledgePolicies (userId: number) {
  const options: QueryOptions = {
    query: gql`
      query getUnAcknowledgePolicies($userId: Int!) {
        policies: message(where: {type: {_eq: PERSISTENT_NOTIFICATION}, _not: {user_message_notification_metadata: {user_id: {_eq: $userId}}}}) {
          id
          type
          data
          createdAt: created_at
          senderUser: user {
            id
            email
          }
          content {
            title
            body
            contentAssets: content_assets {
              asset {
                id
                description
                class
                url
                type
              }
            }
          }
        }
      }
    `,
    variables: {
      userId
    },
    fetchPolicy: 'no-cache'
  }

  return apolloProvider.defaultClient.query(options)
}

export function insertMessageAcknowledge (notificationId: number, userId: number) {
  const options: MutationOptions = {
    mutation: gql`
      mutation insertMessageAcknowledge($object: user_message_notification_metadata_insert_input!) {
        insert_user_message_notification_metadata_one(object: $object, on_conflict: {constraint: user_message_notification_metadata_pkey, update_columns: [is_acknowledged]}) {
          isRead: is_read
          isAcknowledged: is_acknowledged
        }
      }`,
    variables: {
      object: {
        is_read: true,
        is_acknowledged: true,
        user_id: userId,
        message_id: notificationId
      }
    }
  }

  return apolloProvider.defaultClient.mutate(options)
}
